import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { formatMoney } from '../utils/currency';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';

const UserDetails = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [rides, setRides] = useState([]);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      setUser(response.data.data.user);
      setRides(response.data.data.recentRides);
      setCurrencyCode(String(response.data?.currencyCode || response.data?.data?.currencyCode || 'QAR').toUpperCase());
    } catch (error) {
      console.error('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <div className="text-center py-12">User not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/users" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">User Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Info Card */}
        <div className="card">
          <div className="text-center">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-indigo-600 font-bold text-2xl">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </span>
            </div>
            <h2 className="text-xl font-bold">{user.firstName} {user.lastName}</h2>
            <p className="text-gray-500">{user.email}</p>
            <p className="text-gray-500">{user.phone}</p>
            <div className="mt-3">
              <StatusBadge status={user.status} />
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-indigo-600">{user.totalRides || 0}</p>
              <p className="text-sm text-gray-500">Total Rides</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-500">
                ★ {(user.ratingAverage ?? user.rating)?.toFixed(1) ?? '—'}
              </p>
              <p className="text-sm text-gray-500">
                Rating{user.ratingCount > 0 ? ` (${user.ratingCount})` : ''}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{user.savedLocations?.length || 0}</p>
              <p className="text-sm text-gray-500">Saved Places</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{user.paymentMethods?.length || 0}</p>
              <p className="text-sm text-gray-500">Payment Methods</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Rides */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Recent Rides</h3>
        {rides.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Driver</th>
                  <th className="table-header">Route</th>
                  <th className="table-header">Fare</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rides.map((ride) => (
                  <tr key={ride._id}>
                    <td className="table-cell">{new Date(ride.createdAt).toLocaleDateString()}</td>
                    <td className="table-cell">{ride.driver?.firstName} {ride.driver?.lastName}</td>
                    <td className="table-cell max-w-xs truncate">{ride.pickup?.address}</td>
                    <td className="table-cell">{formatMoney(ride.fare?.totalFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</td>
                    <td className="table-cell">
                      <StatusBadge status={ride.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No rides yet</p>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
